/* eslint-disable react/jsx-pascal-case */
// frontend/src/components/InviteGenerator.js
import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config';
import './InviteGenerator.css'; // Подключаем новые стили

// --- SVG Иконки ---
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M9.78 18.65l.28-4.23l7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3L3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.17c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);
// -------------------

// Компонент для генерации пригласительных ссылок
const InviteGenerator = ({ telegramId, onSectionChange }) => {
  const [botLink, setBotLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState('Копировать');
  const [remainingSlots, setRemainingSlots] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCurrentInviteActiveAndUnused, setIsCurrentInviteActiveAndUnused] = useState(false);

  // Получение количества оставшихся инвайтов и активного инвайта
  const fetchInviteStatus = useCallback(async () => {
    if (!telegramId) return;
    setError(null); // Сбрасываем ошибку при проверке
    try {
      const response = await fetch(getApiUrl(`/api/remaining_invites/${telegramId}`));
      if (!response.ok) throw new Error('Не удалось получить информацию об инвайтах');
      const data = await response.json();
      setRemainingSlots(data.remaining_slots);
      
      // Обновляем состояние активного инвайта
      if (data.is_current_invite_active_and_unused && data.active_invite_code) {
        setBotLink(`https://t.me/BroCircleBot?start=${data.active_invite_code}`);
        setIsCurrentInviteActiveAndUnused(true);
      } else {
        setBotLink('');
        setIsCurrentInviteActiveAndUnused(false);
      }
    } catch (err) {
      console.error('Ошибка при получении инвайтов:', err);
      setError('Не удалось загрузить информацию об инвайтах');
      setRemainingSlots(0);
      setIsCurrentInviteActiveAndUnused(false);
    }
  }, [telegramId]);

  useEffect(() => {
    if (telegramId) {
      fetchInviteStatus();
    }
  }, [telegramId, fetchInviteStatus]);

  // Функция генерации нового инвайта
  const handleGenerateInvite = async () => {
    setLoading(true);
    setError(null);
    setGenerateSuccess(false); // Сбрасываем перед новой попыткой

    try {
      const response = await fetch(getApiUrl('/api/generate_invite'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `telegram_id=${telegramId}`,
      });

      if (!response.ok) {
         const errorData = await response.json().catch(() => ({ error: `Ошибка сервера: ${response.status}` }));
         throw new Error(errorData.error || `Не удалось сгенерировать инвайт`);
      }

      const data = await response.json();
      if (!data.invite_code) throw new Error('Ошибка: сервер вернул ответ без инвайт-кода');

      const code = data.invite_code;
      const newBotLink = `https://t.me/BroCircleBot?start=${code}`;
      setBotLink(newBotLink);
      setRemainingSlots(data.remaining_slots);
      setIsCurrentInviteActiveAndUnused(data.is_current_invite_active_and_unused);
      setGenerateSuccess(true);
      setIsModalOpen(true); // Показываем модалку после генерации
      setTimeout(() => setGenerateSuccess(false), 2000);

    } catch (err) {
      console.error('Ошибка:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Функция копирования ссылки
  const copyInviteLink = () => {
    navigator.clipboard.writeText(botLink)
      .then(() => {
        setCopyButtonText('Скопировано!');
        setTimeout(() => setCopyButtonText('Копировать'), 1500);
      })
      .catch(err => {
        console.error('Не удалось скопировать текст: ', err);
        setError('Не удалось скопировать ссылку. Попробуйте вручную.');
      });
  };

  // Функция "Поделиться"
  const shareViaTelegram = () => {
    if (!botLink) return;
    const message = `Привет! Приглашаю тебя в закрытый нетворкинг нового типа, на основе Telegram. Ссылка выше - это инвайт для открытия доступа. Просто переходи по ней и бот автоматически откроет доступ`;
    const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(botLink)}&text=${encodeURIComponent(message)}`;
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.openTelegramLink(telegramShareUrl);
    } else {
      window.open(telegramShareUrl, '_blank');
    }
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="screen-content invite-generator-page">
      <h1 className="page-title">Приглашения</h1>

      {/* Карточка с информацией об инвайтах */}
      <div className="card invite-info-card">
        <div className="invite-count-container">
          <div className="invite-count-number">
            {remainingSlots === null ? '...' : remainingSlots}
          </div>
          <div className="invite-count-label">
            {remainingSlots === 1 ? 'доступный инвайт'
             : (remainingSlots > 1 && remainingSlots < 5) ? 'доступных инвайта'
             : 'доступных инвайтов'}
          </div>
        </div>
        <p className="invite-info-text">
          Вы можете пригласить ограниченное количество друзей. Используйте инвайты с умом!
        </p>
      </div>

      {/* Карточка для генерации и отправки инвайта */}
      <div className="card invite-actions-card">
        <h2>Пригласить друга</h2>
        {error && <div className="error-message"><p>⚠️ {error}</p></div>}
        
        {/* Блок с активным инвайтом */}
        {isCurrentInviteActiveAndUnused && botLink ? (
          <div className="active-invite-section">
            <div className="active-invite-message">
              ✅ <strong>Ваш инвайт-код активен !</strong> <br /> Вы сможете сгенерировать следующий инвайт только <strong>после того, как этот будет использован</strong>. <br /> Зайдите в поиск, мои инвайты - там показаны ваши друзья которых вы пригласили. 
            </div>
            {/*
            <div className="invite-link-display">
              <input 
                type="text" 
                value={botLink} 
                readOnly 
                className="invite-link-input" 
                onClick={(e) => e.target.select()} 
              />
              <button
                onClick={copyInviteLink}
                className="button button-secondary copy-button"
                title="Копировать ссылку"
                disabled={copyButtonText === 'Скопировано!'}
              >
                <CopyIcon />
                <span>{copyButtonText}</span>
              </button>
            </div>
            */}
            <button 
              onClick={shareViaTelegram} 
              className="button button-primary share-button"
            >
              <TelegramIcon />
              <span>Отправить через Telegram</span>
            </button>
          </div>
        ) : (
          /* Кнопка генерации нового инвайта */
          <div className="generate-invite-section">
            {remainingSlots <= 0 ? (
              <div className="no-invites-message">
                <p>Инвайты закончились</p>
              </div>
            ) : (
              <button
                onClick={handleGenerateInvite}
                disabled={loading || generateSuccess || remainingSlots === 0}
                className="button button-primary generate-button"
              >
                {loading ? 'Генерация...' : generateSuccess ? 'Готово!' : 'Сгенерировать инвайт'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Карточка для создания активности */}
      <div className="card activity-promo-card">
        <h3>Активности сообщества</h3>
        <p>Создайте объявление, запрос или предложение, которое будет видно всем пользователям в разделе "Активности".</p>
        <button onClick={() => onSectionChange('create_activity')} className="button button-primary">
          Создать активность
        </button>
      </div>

      {/* Модальное окно с инструкцией */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
               <h3>Пояснения</h3>
               <button onClick={closeModal} className="close-modal-button">✕</button>
            </div>
            <div className="modal-body">
              <p>Как отправлять и обновлять инвайты:</p>
              <ol>
                <li>ВАЖНО! Кнопка генерации нового инвайта не появится, пока не будет активирован предыдущий инвайт.</li>
                <li>Используйте кнопку "Поделиться через Telegram" для быстрой отправки.</li>
                <li>Когда друг перейдет по ссылке, он сможет присоединиться к VINCLE.</li>
                <li>Чтобы появилась кнопка генерации нового инвайта, если инвайт уже активирован, просто нажмите любую кнопку в меню, чтобы обновилась страница инвайтов.</li>
              </ol>
              
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="button button-primary">Понятно</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteGenerator;
